import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { ICON_SIZE } from "../../../theme/tokens";
import { PBJ_ALPHA } from "./colors";
import type { RecommendationId } from "./recommendations.constants";

// The Add button keeps one width whether it reads "Add" or is disabled, so the row doesn't reflow.
const ADD_BUTTON_MIN_WIDTH_PX = 90;

type Props = {
	recId: RecommendationId;
	// Theme-derived categorical accent for this lever (the outlined Add wears it).
	color: string;
	// In the Action Plan already: a settled "Active" confirmation replaces the button.
	isSelected: boolean;
	// A cut too small to commit a whole hour a day — the row explains why in a note under it.
	disabled: boolean;
	onAdd: () => void;
};

/** The lever card's commit affordance: an accent-outlined "Add", or "Active" once the lever is in the plan. */
export const RecommendationAddControl: FC<Props> = ({ recId, color, isSelected, disabled, onAdd }) => {
	const { t } = useTranslation();
	if (isSelected) {
		return (
			<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "success.main", flexShrink: 0 }}>
				<CheckCircleIcon sx={{ fontSize: ICON_SIZE.INLINE }} />
				<Typography variant="caption" sx={{ fontWeight: "bold" }}>
					{t("pbj_model.recommendations.active")}
				</Typography>
			</Stack>
		);
	}
	return (
		<SystemButton
			buttonVariant="secondary"
			size="small"
			onClick={onAdd}
			disabled={disabled}
			leadingIcon={<AddIcon sx={{ fontSize: ICON_SIZE.PILL }} />}
			noFullWidthMobile
			sx={{
				minWidth: ADD_BUTTON_MIN_WIDTH_PX,
				flexShrink: 0,
				color,
				borderColor: alpha(color, PBJ_ALPHA.BORDER),
				"&:hover": { bgcolor: alpha(color, PBJ_ALPHA.FAINT), borderColor: color },
			}}
			data-testid={`pbj-model-recommendation-add-${recId}`}
		>
			{t("pbj_model.recommendations.add")}
		</SystemButton>
	);
};
